import { Routes } from '@angular/router';

import { environment } from '../environments/environment';
import { OPPORTUNITIES_COPY } from './features/opportunities/opportunities.copy';
import { PERRON_COINS_COPY } from './features/perron-coins/perron-coins.copy';
import { RAFFLES_COPY } from './features/raffles/raffles.copy';
import { seoResolver } from './shared/resolvers/seo.resolver';

/**
 * Blog (B58 del spec del blog): la entrada `blog` solo existe con el flag
 * encendido. Con `false`, `/blog` y `/blog/:slug` siguen cayendo en el
 * catch-all. Va con `loadChildren` para que el resolver del artículo,
 * `BlogService` y los mocks no entren en el bundle inicial. Es una función
 * para que los tests prueben los dos valores del flag: Karma compila con
 * `environment.ts`, donde está apagado.
 */
export function blogRoutes(enabled: boolean): Routes {
  if (!enabled) return [];
  return [
    {
      path: 'blog',
      loadChildren: () => import('./features/blog/blog.routes').then((m) => m.BLOG_ROUTES),
    },
  ];
}

/**
 * Cada ruta lleva un `data.seo` con los metadatos por defecto. El resolver
 * global `seoResolver` lee ese objeto y aplica `SeoService.apply()` antes de
 * activar el componente, garantizando meta tags correctos en SSR.
 *
 * Las páginas con datos dinámicos (ficha de producto, catálogo por categoría
 * con nombre del API, búsqueda con `q`) sobreescriben los defaults llamando a
 * `SeoService.apply()` directamente cuando los datos llegan.
 */
export const routes: Routes = [
  {
    path: 'tienda',
    loadComponent: () =>
      import('./features/catalog/catalog-page.component').then(
        (m) => m.CatalogPageComponent,
      ),
    pathMatch: 'full',
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Tienda',
        description:
          'Explora todo el catálogo de Perrón Store: cartas TCG, sobres, productos sellados, accesorios y precompras de las últimas expansiones.',
        type: 'website',
      },
    },
  },
  {
    // Resultados de búsqueda — reusa CatalogPageComponent, que ya lee `q` del
    // queryParamMap y soporta `category=null`. Los ajustes cosméticos (H1,
    // breadcrumb, placeholder) se controlan via `isSearchMode` computed.
    path: 'buscar',
    loadComponent: () =>
      import('./features/catalog/catalog-page.component').then(
        (m) => m.CatalogPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Resultados de búsqueda',
        description:
          'Busca cartas, sobres y productos TCG en el catálogo completo de Perrón Store.',
        type: 'website',
        // Las páginas de resultados de búsqueda no aportan SEO único — evitamos
        // que aparezcan en el índice de Google con thin content/duplicados.
        robots: 'noindex,follow',
      },
    },
  },
  {
    path: 'tienda/precompras',
    loadComponent: () =>
      import(
        './features/tienda/prepurchase-list/prepurchase-list-page.component'
      ).then((m) => m.PrepurchaseListPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Precompras',
        description:
          'Reserva los próximos lanzamientos TCG antes de que salgan: Magic, Pokémon, Yu-Gi-Oh! y más con un 30% de adelanto.',
        type: 'website',
      },
    },
  },
  {
    path: 'tienda/:category/:productSlug',
    loadComponent: () =>
      import('./features/tienda/product-detail/product-detail-page.component').then(
        (m) => m.ProductDetailPageComponent,
      ),
    // El componente aplica SEO con los datos del producto (Title, OG, Product
    // JSON-LD). Sin resolver para no aplicar defaults genéricos que el propio
    // componente sobreescribe.
  },
  {
    path: 'tienda/:category',
    loadComponent: () =>
      import('./features/catalog/catalog-page.component').then(
        (m) => m.CatalogPageComponent,
      ),
    // El componente aplica SEO con el nombre real de la categoría cuando llega.
  },
  {
    // Listado completo de eventos de un juego, del que la landing solo enseña
    // los 6 primeros. Debe ir ANTES de 'juegos/:gameSlug' para que el segmento
    // 'eventos' no lo capture el parámetro.
    path: 'juegos/:gameSlug/eventos',
    loadComponent: () =>
      import('./features/events/events-page.component').then((m) => m.EventsPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Eventos',
        description:
          'Torneos, lanzamientos y precompras de Perrón Store. Consulta fechas, formatos y precios de los próximos eventos.',
        type: 'website',
      },
    },
  },
  {
    // Landing editorial por juego. El `data.seo` es solo la base del SSR: el
    // componente sobreescribe con `meta_title`/`meta_description` del API (o su
    // fallback `name`/`tagline`) en cuanto llega el payload.
    path: 'juegos/:gameSlug',
    loadComponent: () =>
      import('./features/game-section/game-section-page.component').then(
        (m) => m.GameSectionPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Juegos',
        description:
          'Descubre los juegos de cartas que vendemos en Perrón Store: qué son, cómo se juegan y por dónde empezar.',
        type: 'website',
      },
    },
  },
  {
    path: 'nosotros',
    loadComponent: () =>
      import('./features/about/about-page.component').then(
        (m) => m.AboutPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Sobre nosotros',
        description:
          'Conoce a Perrón Store: jugadores y coleccionistas detrás de la tienda TCG en Costa Rica. Nuestra historia, valores y comunidad.',
        type: 'website',
      },
    },
  },
  {
    // Agenda global. Va en ruta propia y no dentro de /comunidad porque esa
    // página es todavía contenido de muestra (MOCK_COMMUNITY, MOCK_PLAYERS):
    // mezclar eventos reales con jugadores inventados leería como que todo lo
    // de esa página es real. Comunidad enlaza aquí.
    path: 'eventos',
    loadComponent: () =>
      import('./features/events/events-page.component').then((m) => m.EventsPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Eventos',
        description:
          'Calendario de torneos, lanzamientos y precompras de Perrón Store en Costa Rica. Consulta fechas, formatos y precios de los próximos eventos TCG.',
        type: 'website',
      },
    },
  },
  {
    path: 'comunidad',
    loadComponent: () =>
      import('./features/community/community-page.component').then(
        (m) => m.CommunityPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Comunidad',
        description:
          'Encuentra jugadores TCG en tu provincia, suscríbete al newsletter y únete a torneos y eventos de la comunidad Perrón Store.',
        type: 'website',
      },
    },
  },
  {
    // Página pública de Perrón Coins (A0·web): SSR con los ajustes de la API vía TransferState. Indexable.
    path: 'perron-coins',
    loadComponent: () =>
      import('./features/perron-coins/perron-coins-page.component').then((m) => m.PerronCoinsPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: PERRON_COINS_COPY.SEO_TITLE,
        description: PERRON_COINS_COPY.SEO_DESCRIPTION,
        type: 'website',
      },
    },
  },
  {
    // Rifas de Perrón Coins (B1·web, M41): SSR con ajustes y lista vía TransferState. Indexable. La página mira el flag
    // `raffles` (D59); la ruta existe siempre.
    path: 'rifas',
    loadComponent: () => import('./features/raffles/raffles-page.component').then((m) => m.RafflesPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: RAFFLES_COPY.SEO_TITLE,
        description: RAFFLES_COPY.SEO_DESCRIPTION,
        type: 'website',
      },
    },
  },
  {
    // Ficha de una rifa con la compra (B1·web, W5-W6): SSR por `rifas/**`. El `data.seo` es solo la base: la ficha lo
    // sobreescribe con la rifa (calco de `juegos/:gameSlug`) o pone `noindex` si no existe o no está disponible.
    path: 'rifas/:id',
    loadComponent: () =>
      import('./features/raffles/raffle-detail-page.component').then((m) => m.RaffleDetailPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: RAFFLES_COPY.SEO_TITLE,
        description: RAFFLES_COPY.SEO_DESCRIPTION,
        type: 'website',
      },
    },
  },
  {
    // Oportunidades con Coins (B2·web, D121): SSR con ajustes y lista vía TransferState. Indexable. La página mira el
    // flag `opportunities` (D123); la ruta existe siempre. Sin ficha propia (D128): las tarjetas llevan al producto.
    path: 'oportunidades',
    loadComponent: () =>
      import('./features/opportunities/opportunities-page.component').then((m) => m.OpportunitiesPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: OPPORTUNITIES_COPY.SEO_TITLE,
        description: OPPORTUNITIES_COPY.SEO_DESCRIPTION,
        type: 'website',
      },
    },
  },
  {
    path: 'contacto',
    loadComponent: () =>
      import('./features/contact/contact-page.component').then(
        (m) => m.ContactPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Contacto',
        description:
          'Escríbenos por WhatsApp, correo o visítanos en Plaza Del Castillo, Zapote, San José. Atención al cliente Perrón Store.',
        type: 'website',
      },
    },
  },
  // ── Páginas legales (estáticas, data-driven; contenido en features/legal) ──
  {
    path: 'terminos-y-condiciones',
    loadComponent: () =>
      import('./features/legal/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      legalDoc: 'terminos-y-condiciones',
      seo: {
        title: 'Términos y Condiciones',
        description:
          'Términos y condiciones de uso de Perrón Store: compras, pagos, apartados, preventas y derecho de retracto en Costa Rica.',
        type: 'website',
      },
    },
  },
  {
    path: 'privacidad',
    loadComponent: () =>
      import('./features/legal/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      legalDoc: 'privacidad',
      seo: {
        title: 'Política de Privacidad',
        description:
          'Cómo Perrón Store trata y protege tus datos personales conforme a la Ley 8968 y la PRODHAB en Costa Rica.',
        type: 'website',
      },
    },
  },
  {
    path: 'cambios-y-devoluciones',
    loadComponent: () =>
      import('./features/legal/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      legalDoc: 'cambios-y-devoluciones',
      seo: {
        title: 'Cambios y Devoluciones',
        description:
          'Política de cambios, devoluciones y reembolsos de Perrón Store, con el derecho de retracto de la Ley 7472.',
        type: 'website',
      },
    },
  },
  {
    path: 'envios',
    loadComponent: () =>
      import('./features/legal/legal-page.component').then(
        (m) => m.LegalPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      legalDoc: 'envios',
      seo: {
        title: 'Política de Envíos',
        description:
          'Cobertura, medios, costos y plazos de envío de Perrón Store en Costa Rica, incluido el retiro en tienda.',
        type: 'website',
      },
    },
  },
  {
    path: '',
    loadComponent: () =>
      import('./features/home/home.component').then(
        (m) => m.HomePageComponent,
      ),
    pathMatch: 'full',
    resolve: { seo: seoResolver },
    data: {
      seo: {
        // Title sin sufijo: ya contiene la marca explícitamente.
        title: 'Perrón Store — Trading Card Games en Costa Rica',
        rawTitle: true,
        description:
          'Tienda especializada en TCG (Magic, Pokémon, Yu-Gi-Oh!, One Piece y más) en Costa Rica. Compra cartas sueltas, sobres, productos sellados y reserva precompras.',
        type: 'website',
      },
    },
  },
  {
    path: 'reset-password/:id/:token',
    loadComponent: () =>
      import(
        './features/auth/reset-password/reset-password-page.component'
      ).then((m) => m.ResetPasswordPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Restablecer contraseña',
        description: 'Restablece la contraseña de tu cuenta Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    path: 'verificar-cuenta/:id/:token',
    loadComponent: () =>
      import(
        './features/auth/email-verification/email-verification-page.component'
      ).then((m) => m.EmailVerificationPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Verificar cuenta',
        description: 'Confirma tu correo electrónico para activar tu cuenta Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    path: 'checkout',
    loadComponent: () =>
      import('./features/checkout/checkout-page.component').then(
        (m) => m.CheckoutPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Checkout',
        description: 'Completa tu compra en Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    // Paso de pago con tarjeta del flujo DIFERIDO (Pasarela · 5). El preorder NO existe todavía:
    // el intent cuelga de una `checkout_session` (token en la URL). El componente hace el bootstrap
    // (router-state o fetch de la sesión) y redirige a /checkout/exito si ya se materializó. No lleva
    // resolver de preorder porque aún no hay pedido.
    path: 'checkout/pago/sesion/:sessionToken',
    loadComponent: () =>
      import('./features/checkout/payment/checkout-payment-page.component').then(
        (m) => m.CheckoutPaymentPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Pago',
        description: 'Completa el pago de tu pedido en Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    // Paso de pago con TARJETA del abono (30 %) de una PRECOMPRA (Bloque 3). El intent cuelga de una
    // `prepurchase_session` (token en la URL); la `Prepurchase` NO existe hasta aprobarse el cobro. No
    // hay carrito ni pedido: el componente rehidrata por token tras un refresh y muestra el éxito inline.
    path: 'precompra/pago/sesion/:sessionToken',
    loadComponent: () =>
      import('./features/checkout/payment/prepurchase-payment-page.component').then(
        (m) => m.PrepurchasePaymentPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Pago de precompra',
        description: 'Completa el abono de tu precompra en Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    path: 'checkout/exito/:token',
    loadComponent: () =>
      import('./features/checkout/success/checkout-success-page.component').then(
        (m) => m.CheckoutSuccessPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Compra confirmada',
        description: 'Gracias por tu compra en Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    // Vista pública de detalle de pedido. El cliente abre este enlace desde el
    // botón "VER PEDIDO" del correo de confirmación, cuya URL hardcodea
    // `https://perronstore.com/pedido/{token}`. El token (Str::random(60)) es
    // alfanumérico, así que no requiere encoding especial en el path.
    path: 'pedido/:token',
    loadComponent: () =>
      import(
        './features/preorder/preorder-detail/preorder-detail-page.component'
      ).then((m) => m.PreorderDetailPageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Detalle de tu pedido',
        description:
          'Consulta el contenido, los pagos y el estado de tu pedido en Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    path: 'comparar',
    loadComponent: () =>
      import('./features/compare/compare-page.component').then(
        (m) => m.ComparePageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Comparar productos',
        description: 'Compara productos TCG lado a lado en Perrón Store.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    path: 'avisarme/unsubscribe/:token',
    loadComponent: () =>
      import(
        './features/tienda/stock-alert-unsubscribe/stock-alert-unsubscribe-page.component'
      ).then((m) => m.StockAlertUnsubscribePageComponent),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Cancelar alerta',
        description: 'Cancelar suscripción a alertas de stock.',
        robots: 'noindex,nofollow',
      },
    },
  },
  {
    // Destino de `BREVO_DOI_REDIRECT_URL` (B9, W5): el suscriptor llega aquí tras
    // confirmar el doble opt-in. Estática, sin API; `noindex,follow` y fuera del
    // sitemap (no está en la lista blanca de `tools/build-sitemap.mjs`).
    path: 'newsletter/confirmada',
    loadComponent: () =>
      import('./features/newsletter/newsletter-confirmed-page.component').then(
        (m) => m.NewsletterConfirmedPageComponent,
      ),
    resolve: { seo: seoResolver },
    data: {
      seo: {
        title: 'Suscripción confirmada',
        description: 'Tu suscripción al newsletter de Perrón Store está confirmada.',
        robots: 'noindex,follow',
      },
    },
  },
  {
    // Perfil de usuario (B10, B26): pestañas como rutas hijas en `profile.routes.ts`. Nada del perfil en SSR
    // (`RenderMode.Client` en `app.routes.server.ts`) y fuera de buscadores (`noindex,nofollow` + robots.txt).
    // Va antes del blog para que el blog siga justo antes de `**`.
    path: 'perfil',
    loadChildren: () => import('./features/profile/profile.routes').then((m) => m.PROFILE_ROUTES),
    resolve: { seo: seoResolver },
    data: { seo: { title: 'Mi perfil', robots: 'noindex,nofollow' } },
  },
  ...blogRoutes(environment.blogEnabled),
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
